import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const IconCheck = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12l5 5L20 7"/>
  </svg>
);
const IconStar = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#d4a843" stroke="none">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);
const IconRings = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="13" r="4"/>
    <circle cx="16" cy="13" r="4"/>
    <path d="M8 9V6a2 2 0 014 0v1"/>
    <path d="M16 9V6a2 2 0 014 0v3"/>
  </svg>
);
const IconBuilding = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="1"/>
    <path d="M3 9h18M3 15h18M9 3v18M15 3v18"/>
  </svg>
);
const IconGlass = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3l-2 8a4 4 0 008 0L12 3H8z"/>
    <path d="M10 15v6M7 21h6"/>
    <path d="M16 5l1 5a3 3 0 006 0l-1-5"/>
  </svg>
);
const IconMic = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="11" rx="3"/>
    <path d="M5 10a7 7 0 0014 0M12 19v3M8 22h8"/>
  </svg>
);
const IconCap = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 10l10-6 10 6-10 6-10-6z"/>
    <path d="M6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5"/>
    <path d="M22 10v6"/>
  </svg>
);
const IconCamera = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);
const IconTarget = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="6"/>
    <circle cx="12" cy="12" r="2"/>
  </svg>
);
const IconShield = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l8 3v6c0 5-3.5 9-8 11C7.5 20 4 16 4 11V5l8-3z"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);
const IconHandshake = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11l5-5h4l4 4h5"/>
    <path d="M3 13l5 5h3l6-6"/>
    <path d="M12 8l-2 2 4 4 2-2"/>
  </svg>
);
const IconClock = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="M12 7v5l3 3"/>
  </svg>
);
const IconEscrow = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="M12 6v1.5m0 9V18m3-5.5c0-1.4-1.3-2.5-3-2.5s-3 1.1-3 2.5 1.3 2.5 3 2.5 3 1.1 3 2.5-1.3 2.5-3 2.5-3-1.1-3-2.5"/>
  </svg>
);

const Events = () => {
  useEffect(() => {
    const nav = document.getElementById('ev-nav');
    const onScroll = () => nav?.classList.toggle('scrolled', window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const reveals = document.querySelectorAll('.ev-reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => { if (entry.isIntersecting) entry.target.classList.add('visible'); });
    }, { threshold: 0.12 });
    reveals.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Event Photography in Denver for Venues &amp; Corporate Events | FrameFinder</title>
        <meta name="description" content="Find vetted event photographers in Denver for weddings, corporate events, galas, and more. Browse talent, post a project, and book with confidence on FrameFinder." />
        <link rel="canonical" href="https://www.framefinder.io/events" />
      </Helmet>
      <style>{`
        .ev-wrap *, .ev-wrap *::before, .ev-wrap *::after { box-sizing: border-box; }
        .ev-wrap {
          --ink:#0e0e0e;--ink-soft:#5a5a5a;--ink-faint:#a0a0a0;--paper:#f7f5f2;--white:#ffffff;
          --accent:#d4a843;--accent-soft:#f5ead0;--accent-deep:#8a6a1a;
          --border:#e4e1db;--border-deep:#c8c3bb;
          --shadow-sm:0 1px 4px rgba(14,14,14,0.06);--shadow-md:0 4px 20px rgba(14,14,14,0.08);--shadow-lg:0 12px 48px rgba(14,14,14,0.14);
          --r-sm:8px;--r-md:14px;--r-lg:24px;--r-pill:100px;
          font-family:'DM Sans',sans-serif;background:var(--paper);color:var(--ink);-webkit-font-smoothing:antialiased;line-height:1.5;overflow-x:hidden;
        }
        /* NAV */
        .ev-nav { position:fixed;top:0;left:0;right:0;z-index:100;padding:0 40px;height:64px;display:flex;align-items:center;justify-content:space-between;background:rgba(247,245,242,0.88);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border-bottom:1px solid transparent;transition:border-color 0.3s; }
        .ev-nav.scrolled { border-bottom-color:var(--border); }
        .ev-nav-logo { font-family:'Instrument Serif',serif;font-size:22px;color:var(--ink);text-decoration:none;letter-spacing:-0.3px; }
        .ev-nav-right { display:flex;align-items:center;gap:12px; }
        .ev-nav-link { font-size:13px;font-weight:500;color:var(--ink-soft);text-decoration:none;transition:color 0.15s; }
        .ev-nav-link:hover { color:var(--ink); }
        .ev-btn { display:inline-flex;align-items:center;gap:6px;font-family:'DM Sans',sans-serif;font-weight:500;border:none;cursor:pointer;text-decoration:none;border-radius:var(--r-pill);transition:transform 0.15s,box-shadow 0.15s,opacity 0.15s;white-space:nowrap; }
        .ev-btn:hover { transform:translateY(-1px); }
        .ev-btn-primary { background:var(--ink);color:var(--white);box-shadow:0 2px 12px rgba(14,14,14,0.18);font-size:14px;padding:10px 22px; }
        .ev-btn-ghost { background:var(--white);color:var(--ink);border:1px solid var(--border);font-size:14px;padding:10px 22px; }
        .ev-btn-ghost:hover { background:var(--paper); }
        .ev-btn-accent { background:var(--accent);color:var(--ink);font-size:15px;padding:14px 30px; }
        .ev-btn-accent:hover { opacity:0.9; }
        .ev-btn-hero-primary { background:var(--white);color:var(--ink);font-size:15px;padding:14px 30px;box-shadow:0 4px 20px rgba(0,0,0,0.3); }
        .ev-btn-hero-ghost { background:transparent;color:rgba(255,255,255,0.65);border:1px solid rgba(255,255,255,0.18);font-size:15px;padding:14px 30px; }
        .ev-btn-hero-ghost:hover { border-color:rgba(255,255,255,0.4);color:var(--white); }
        /* HERO */
        .ev-hero { min-height:100vh;background:var(--ink);display:flex;flex-direction:column;justify-content:center;position:relative;overflow:hidden;padding:120px 40px 80px; }
        .ev-hero::before { content:'';position:absolute;right:-200px;top:-200px;width:900px;height:900px;background:radial-gradient(circle,rgba(212,168,67,0.09) 0%,transparent 65%);pointer-events:none; }
        .ev-hero::after { content:'';position:absolute;left:-100px;bottom:-100px;width:600px;height:600px;background:radial-gradient(circle,rgba(212,168,67,0.05) 0%,transparent 65%);pointer-events:none; }
        .ev-hero-inner { max-width:1120px;margin:0 auto;width:100%;position:relative;z-index:1;display:grid;grid-template-columns:1fr 400px;gap:80px;align-items:center; }
        .ev-hero-eyebrow { display:inline-flex;align-items:center;gap:8px;font-size:11px;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:var(--accent);margin-bottom:24px;opacity:0;animation:evFadeUp 0.6s ease 0.1s both; }
        .ev-eyebrow-dot { width:6px;height:6px;border-radius:50%;background:var(--accent);flex-shrink:0; }
        .ev-hero-title { font-family:'Instrument Serif',serif;font-weight:400;font-size:clamp(44px,5.5vw,72px);line-height:1.0;letter-spacing:-2px;color:var(--white);margin-bottom:24px;opacity:0;animation:evFadeUp 0.6s ease 0.2s both; }
        .ev-hero-title em { font-style:italic;color:rgba(255,255,255,0.38); }
        .ev-hero-subtitle { font-size:17px;line-height:1.65;color:rgba(255,255,255,0.55);max-width:500px;margin-bottom:40px;opacity:0;animation:evFadeUp 0.6s ease 0.3s both; }
        .ev-hero-ctas { display:flex;align-items:center;gap:12px;flex-wrap:wrap;opacity:0;animation:evFadeUp 0.6s ease 0.4s both; }
        .ev-hero-trust { margin-top:48px;display:flex;align-items:center;gap:20px;flex-wrap:wrap;opacity:0;animation:evFadeUp 0.6s ease 0.5s both; }
        .ev-trust-item { display:flex;align-items:center;gap:8px;font-size:13px;color:rgba(255,255,255,0.4); }
        .ev-trust-icon { color:var(--accent);font-size:14px; }
        .ev-trust-divider { width:1px;height:16px;background:rgba(255,255,255,0.12); }
        /* Hero visual */
        .ev-hero-visual { position:relative;height:500px;opacity:0;animation:evFadeUp 0.7s ease 0.35s both; }
        .ev-card { position:absolute;background:var(--white);border-radius:var(--r-lg);overflow:hidden;box-shadow:var(--shadow-lg); }
        .ev-card-main { width:340px;right:0;top:0; }
        .ev-card-secondary { width:230px;left:0;bottom:40px;box-shadow:0 8px 40px rgba(0,0,0,0.25); }
        .ev-card-header { padding:18px 20px 14px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between; }
        .ev-card-title { font-size:14px;font-weight:600;color:var(--ink); }
        .ev-type-badge { font-size:10px;font-weight:700;padding:3px 10px;border-radius:var(--r-pill);background:var(--accent-soft);color:var(--accent-deep);border:1px solid #e8d49a; }
        .ev-upcoming-list { padding:8px 20px 4px; }
        .ev-upcoming-item { display:flex;align-items:center;gap:12px;padding:11px 0;border-bottom:1px solid var(--border); }
        .ev-upcoming-item:last-child { border-bottom:none; }
        .ev-upcoming-date { width:36px;text-align:center;flex-shrink:0; }
        .ev-upcoming-date-num { font-size:16px;font-weight:700;color:var(--ink);line-height:1; }
        .ev-upcoming-date-mon { font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:var(--ink-faint); }
        .ev-upcoming-info { flex:1; }
        .ev-upcoming-name { font-size:12px;font-weight:600;color:var(--ink); }
        .ev-upcoming-type { font-size:11px;color:var(--ink-faint); }
        .ev-status-pill { font-size:10px;font-weight:600;padding:3px 8px;border-radius:var(--r-pill);white-space:nowrap; }
        .ev-status-filled { background:#f0fdf4;color:#166534;border:1px solid #bbf7d0; }
        .ev-status-open { background:var(--accent-soft);color:var(--accent-deep);border:1px solid #e8d49a; }
        .ev-secondary-body { padding:14px 18px; }
        .ev-secondary-detail { display:flex;align-items:center;gap:8px;font-size:12px;color:var(--ink-soft);margin-bottom:8px; }
        .ev-secondary-detail:last-child { margin-bottom:0; }
        .ev-secondary-footer { padding:12px 18px;border-top:1px solid var(--border);display:flex;align-items:center;justify-content:space-between; }
        .ev-float-pill { position:absolute;top:-18px;left:50%;transform:translateX(-50%);background:var(--accent);color:var(--ink);font-size:11px;font-weight:700;padding:6px 16px;border-radius:var(--r-pill);white-space:nowrap;box-shadow:0 4px 16px rgba(212,168,67,0.4);animation:evFloatPill 3s ease-in-out infinite; }
        @keyframes evFloatPill { 0%,100%{transform:translateX(-50%) translateY(0);}50%{transform:translateX(-50%) translateY(-6px);} }
        /* SECTIONS */
        .ev-section { padding:100px 40px; }
        .ev-section-inner { max-width:1120px;margin:0 auto; }
        .ev-section-eyebrow { font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--ink-faint);margin-bottom:12px; }
        .ev-section-title { font-family:'Instrument Serif',serif;font-weight:400;font-size:clamp(32px,3.5vw,48px);letter-spacing:-1.5px;line-height:1.05;color:var(--ink);margin-bottom:16px; }
        .ev-section-title em { font-style:italic;color:var(--ink-soft); }
        .ev-section-desc { font-size:16px;color:var(--ink-soft);max-width:540px;line-height:1.7; }
        /* PAIN */
        .ev-pain-section { background:var(--white);border-top:1px solid var(--border);border-bottom:1px solid var(--border);padding:80px 40px; }
        .ev-pain-grid { display:grid;grid-template-columns:repeat(3,1fr);gap:1px;background:var(--border);border:1px solid var(--border);border-radius:var(--r-lg);overflow:hidden;margin-top:56px; }
        .ev-pain-item { background:var(--white);padding:40px 36px; }
        .ev-pain-number { font-family:'Instrument Serif',serif;font-size:48px;letter-spacing:-2px;color:var(--border-deep);line-height:1;margin-bottom:16px; }
        .ev-pain-title { font-size:17px;font-weight:600;color:var(--ink);margin-bottom:10px;letter-spacing:-0.2px; }
        .ev-pain-body { font-size:14px;color:var(--ink-soft);line-height:1.7; }
        /* EVENT TYPES */
        .ev-types-section { background:var(--paper);padding:100px 40px; }
        .ev-types-grid { display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-top:56px; }
        .ev-type-card { background:var(--white);border:1px solid var(--border);border-radius:var(--r-lg);padding:32px 28px;transition:transform 0.2s,box-shadow 0.2s,border-color 0.2s; }
        .ev-type-card:hover { transform:translateY(-3px);box-shadow:var(--shadow-md);border-color:var(--border-deep); }
        .ev-type-icon { font-size:28px;margin-bottom:16px; }
        .ev-type-name { font-size:16px;font-weight:600;color:var(--ink);margin-bottom:8px;letter-spacing:-0.2px; }
        .ev-type-desc { font-size:13px;color:var(--ink-soft);line-height:1.65;margin-bottom:16px; }
        .ev-type-spec { font-size:11px;font-weight:600;color:var(--ink-faint);padding-top:16px;border-top:1px solid var(--border); }
        /* HOW IT WORKS */
        .ev-how-section { background:var(--white);border-top:1px solid var(--border);padding:100px 40px; }
        .ev-steps-grid { display:grid;grid-template-columns:repeat(4,1fr);gap:24px;margin-top:64px; }
        .ev-step-num { width:56px;height:56px;border-radius:50%;background:var(--paper);border:1px solid var(--border);display:flex;align-items:center;justify-content:center;font-family:'Instrument Serif',serif;font-size:22px;color:var(--ink-soft);margin-bottom:20px; }
        .ev-step-num-active { background:var(--ink);border-color:var(--ink);color:var(--white); }
        .ev-step-title { font-size:15px;font-weight:600;color:var(--ink);margin-bottom:8px;letter-spacing:-0.2px; }
        .ev-step-body { font-size:13px;color:var(--ink-soft);line-height:1.65; }
        /* WHY */
        .ev-why-section { background:var(--ink);padding:100px 40px;position:relative;overflow:hidden; }
        .ev-why-section::before { content:'';position:absolute;right:-200px;top:-200px;width:800px;height:800px;background:radial-gradient(circle,rgba(212,168,67,0.07) 0%,transparent 65%);pointer-events:none; }
        .ev-why-grid { display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:start;position:relative;z-index:1; }
        .ev-why-left .ev-section-title { color:var(--white); }
        .ev-why-left .ev-section-title em { color:rgba(255,255,255,0.3); }
        .ev-why-left .ev-section-desc { color:rgba(255,255,255,0.5); }
        .ev-why-left .ev-section-eyebrow { color:rgba(255,255,255,0.25); }
        .ev-why-features { display:flex;flex-direction:column;gap:2px;margin-top:8px; }
        .ev-why-feature { display:flex;align-items:flex-start;gap:16px;padding:24px;border-radius:var(--r-md);border:1px solid transparent;transition:background 0.2s,border-color 0.2s; }
        .ev-why-feature:hover { background:rgba(255,255,255,0.04);border-color:rgba(255,255,255,0.08); }
        .ev-why-feature-icon { width:40px;height:40px;border-radius:var(--r-sm);background:rgba(212,168,67,0.12);display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0; }
        .ev-why-feature-title { font-size:15px;font-weight:600;color:var(--white);margin-bottom:6px;letter-spacing:-0.2px; }
        .ev-why-feature-body { font-size:13px;color:rgba(255,255,255,0.45);line-height:1.65; }
        /* PHOTOGRAPHERS */
        .ev-photographers-section { padding:100px 40px;background:var(--white);border-top:1px solid var(--border); }
        .ev-photo-grid { display:grid;grid-template-columns:repeat(3,1fr);gap:20px;margin-top:56px; }
        .ev-photo-card { background:var(--paper);border:1px solid var(--border);border-radius:var(--r-lg);overflow:hidden;transition:transform 0.2s,box-shadow 0.2s; }
        .ev-photo-card:hover { transform:translateY(-4px);box-shadow:var(--shadow-lg); }
        .ev-photo-card-img { height:160px;display:flex;align-items:center;justify-content:center;font-size:40px; }
        .ev-img-1 { background:linear-gradient(135deg,#d8ccc0 0%,#c0b0a0 100%); }
        .ev-img-2 { background:linear-gradient(135deg,#c0ccd8 0%,#a0b4c8 100%); }
        .ev-img-3 { background:linear-gradient(135deg,#ccc0d8 0%,#b0a0c8 100%); }
        .ev-photo-card-body { padding:18px 20px; }
        .ev-photo-card-name { font-size:15px;font-weight:600;color:var(--ink);margin-bottom:4px; }
        .ev-photo-card-spec { font-size:12px;color:var(--ink-faint);display:flex;align-items:center;gap:6px;margin-bottom:12px; }
        .ev-photo-card-tags { display:flex;gap:6px;flex-wrap:wrap; }
        .ev-tag { font-size:11px;font-weight:500;padding:4px 10px;border-radius:var(--r-pill);background:var(--white);border:1px solid var(--border);color:var(--ink-soft); }
        .ev-photo-card-footer { padding:14px 20px;border-top:1px solid var(--border);display:flex;align-items:center;justify-content:space-between; }
        .ev-photo-card-rate { font-size:14px;font-weight:700;color:var(--ink); }
        .ev-avail { display:flex;align-items:center;gap:5px;font-size:11px;font-weight:600;color:#166534; }
        .ev-avail-dot { width:6px;height:6px;border-radius:50%;background:#4ade80; }
        /* CTA */
        .ev-cta-section { padding:100px 40px;background:var(--ink);position:relative;overflow:hidden; }
        .ev-cta-section::before { content:'';position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:1000px;height:600px;background:radial-gradient(ellipse,rgba(212,168,67,0.08) 0%,transparent 65%);pointer-events:none; }
        .ev-cta-inner { max-width:680px;margin:0 auto;text-align:center;position:relative;z-index:1; }
        .ev-cta-eyebrow { font-size:11px;font-weight:600;letter-spacing:2px;text-transform:uppercase;color:var(--accent);margin-bottom:20px; }
        .ev-cta-title { font-family:'Instrument Serif',serif;font-weight:400;font-size:clamp(36px,4vw,56px);letter-spacing:-2px;line-height:1.0;color:var(--white);margin-bottom:20px; }
        .ev-cta-title em { font-style:italic;color:rgba(255,255,255,0.3); }
        .ev-cta-body { font-size:16px;color:rgba(255,255,255,0.5);line-height:1.7;margin-bottom:40px; }
        .ev-cta-acts { display:flex;justify-content:center;gap:12px;flex-wrap:wrap; }
        .ev-cta-disclaimer { font-size:12px;color:rgba(255,255,255,0.25);margin-top:16px; }
        /* FOOTER */
        .ev-footer { background:#080808;padding:40px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:16px;border-top:1px solid rgba(255,255,255,0.06); }
        .ev-footer-logo { font-family:'Instrument Serif',serif;font-size:20px;color:var(--white);text-decoration:none; }
        .ev-footer-links { display:flex;gap:24px; }
        .ev-footer-link { font-size:13px;color:rgba(255,255,255,0.3);text-decoration:none;transition:color 0.15s; }
        .ev-footer-link:hover { color:rgba(255,255,255,0.7); }
        .ev-footer-copy { font-size:12px;color:rgba(255,255,255,0.2); }
        /* REVEAL */
        .ev-reveal { opacity:0;transform:translateY(28px);transition:opacity 0.6s ease,transform 0.6s ease; }
        .ev-reveal.visible { opacity:1;transform:translateY(0); }
        .ev-reveal-d1 { transition-delay:0.1s; }
        .ev-reveal-d2 { transition-delay:0.2s; }
        .ev-reveal-d3 { transition-delay:0.3s; }
        @keyframes evFadeUp { from{opacity:0;transform:translateY(24px);}to{opacity:1;transform:translateY(0);} }
        /* RESPONSIVE */
        @media (max-width:960px) {
          .ev-hero-inner,.ev-why-grid { grid-template-columns:1fr;gap:48px; }
          .ev-hero-visual { height:360px; }
          .ev-steps-grid,.ev-types-grid { grid-template-columns:repeat(2,1fr); }
          .ev-photo-grid { grid-template-columns:repeat(2,1fr); }
          .ev-pain-grid { grid-template-columns:1fr; }
        }
        @media (max-width:600px) {
          .ev-nav { padding:0 20px; }
          .ev-nav-link { display:none; }
          .ev-section,.ev-pain-section,.ev-why-section,.ev-types-section,.ev-how-section,.ev-photographers-section,.ev-cta-section { padding:64px 20px; }
          .ev-hero { padding:100px 20px 64px; }
          .ev-steps-grid,.ev-photo-grid,.ev-types-grid { grid-template-columns:1fr; }
          .ev-hero-visual { display:none; }
          .ev-footer { padding:32px 20px;flex-direction:column;align-items:flex-start; }
        }
      `}</style>

      <div className="ev-wrap">
        {/* NAV */}
        <nav className="ev-nav" id="ev-nav">
          <Link className="ev-nav-logo" to="/">FrameFinder</Link>
          <div className="ev-nav-right">
            <Link to="/photographers" className="ev-nav-link">Browse photographers</Link>
            <Link to="/login" className="ev-btn ev-btn-ghost" style={{fontSize:'13px',padding:'8px 18px'}}>Log in</Link>
            <Link to="/signup" className="ev-btn ev-btn-primary" style={{fontSize:'13px',padding:'8px 18px'}}>Get started →</Link>
          </div>
        </nav>

        {/* HERO */}
        <section className="ev-hero">
          <div className="ev-hero-inner">
            <div>
              <div className="ev-hero-eyebrow"><span className="ev-eyebrow-dot"></span>Event photography · Denver, CO</div>
              <h1 className="ev-hero-title">Find the Right Event Photographer<br />for <em>Any Denver Venue</em></h1>
              <p className="ev-hero-subtitle">Weddings, corporate retreats, private dinners, galas — every event has different photography needs. FrameFinder has vetted Denver photographers who specialize in all of it.</p>
              <div className="ev-hero-ctas">
                <Link to="/signup" className="ev-btn ev-btn-hero-primary">Post an event shoot →</Link>
                <Link to="/photographers" className="ev-btn ev-btn-hero-ghost">Browse photographers</Link>
              </div>
              <div className="ev-hero-trust">
                <div className="ev-trust-item"><span className="ev-trust-icon"><IconCheck /></span>Vetted photographers</div>
                <div className="ev-trust-divider"></div>
                <div className="ev-trust-item"><span className="ev-trust-icon"><IconCheck /></span>Escrow payments</div>
                <div className="ev-trust-divider"></div>
                <div className="ev-trust-item"><span className="ev-trust-icon"><IconCheck /></span>Free during beta</div>
              </div>
            </div>
            <div className="ev-hero-visual">
              <div className="ev-card ev-card-main">
                <div className="ev-card-header">
                  <div className="ev-card-title">Upcoming events</div>
                  <div className="ev-type-badge">3 this month</div>
                </div>
                <div className="ev-upcoming-list">
                  {[
                    { num:'12', mon:'Apr', name:'Morrison Wedding Reception', type:'Wedding · Full evening', status:'filled', label:'Filled' },
                    { num:'18', mon:'Apr', name:'Venture Capital Summit', type:'Corporate · Half day', status:'filled', label:'Filled' },
                    { num:'24', mon:'Apr', name:'Garcia Birthday Gala', type:'Private event · Evening', status:'open', label:'Open' },
                  ].map((item, i) => (
                    <div className="ev-upcoming-item" key={i}>
                      <div className="ev-upcoming-date">
                        <div className="ev-upcoming-date-num">{item.num}</div>
                        <div className="ev-upcoming-date-mon">{item.mon}</div>
                      </div>
                      <div className="ev-upcoming-info">
                        <div className="ev-upcoming-name">{item.name}</div>
                        <div className="ev-upcoming-type">{item.type}</div>
                      </div>
                      <div className={`ev-status-pill ev-status-${item.status}`}>{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="ev-card ev-card-secondary" style={{position:'relative'}}>
                <div className="ev-float-pill" style={{display:'flex',alignItems:'center',gap:'5px'}}><IconCheck size={11} /> Photographer confirmed</div>
                <div className="ev-card-header" style={{padding:'14px 18px 12px'}}>
                  <div className="ev-card-title" style={{fontSize:'13px'}}>Morrison Wedding</div>
                </div>
                <div className="ev-secondary-body">
                  <div className="ev-secondary-detail"><IconCamera size={13} /> Jessica Wong</div>
                  <div className="ev-secondary-detail"><IconStar size={13} /> 5.0 · Premier</div>
                  <div className="ev-secondary-detail"><IconClock /> 6pm – 11pm</div>
                </div>
                <div className="ev-secondary-footer">
                  <div className="ev-status-pill ev-status-filled" style={{display:'flex',alignItems:'center',gap:'5px'}}>
                    <span style={{width:'6px',height:'6px',borderRadius:'50%',background:'#4ade80',display:'inline-block'}}></span>Confirmed
                  </div>
                  <span style={{fontSize:'12px',color:'var(--ink-faint)'}}>$220/hr</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PAIN */}
        <section className="ev-pain-section">
          <div className="ev-section-inner">
            <div className="ev-reveal">
              <div className="ev-section-eyebrow">The problem</div>
              <h2 className="ev-section-title">Not every event needs the <em>same photographer.</em></h2>
            </div>
            <div className="ev-pain-grid ev-reveal" style={{transitionDelay:'0.1s'}}>
              {[
                { num:'01', title:"A wedding photographer isn't a corporate photographer.", body:"The skills are different, the approach is different, and the deliverables are different. Using the wrong specialist for the event shows in the final photos — and your clients notice." },
                { num:'02', title:"Finding the right fit takes time you don't have.", body:"Vetting photographers, checking availability, comparing rates, confirming details — that's a significant time investment every time a new event comes through. It shouldn't be." },
                { num:'03', title:"No agreed scope means no protection when things go wrong.", body:"A no-show, a missed deadline, a dispute over what was supposed to be delivered. Without everyone on the same page upfront, there's nothing to stand on when something goes sideways." },
              ].map((p, i) => (
                <div className="ev-pain-item" key={i}>
                  <div className="ev-pain-number">{p.num}</div>
                  <div className="ev-pain-title">{p.title}</div>
                  <div className="ev-pain-body">{p.body}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EVENT TYPES */}
        <section className="ev-types-section">
          <div className="ev-section-inner">
            <div className="ev-reveal">
              <div className="ev-section-eyebrow">Specializations</div>
              <h2 className="ev-section-title">Photographers built for <em>your kind of event.</em></h2>
              <p className="ev-section-desc">Every photographer on FrameFinder lists their specializations. Whatever event you're staffing, you can find someone whose work is built for exactly that context.</p>
            </div>
            <div className="ev-types-grid">
              {[
                { icon:<IconRings />, name:'Weddings', desc:"Photographers who know how to blend into a room, capture candid moments, and tell the full story of a day without getting in the way.", spec:'Ceremony · Reception · Portraits · Detail shots', d:'d1' },
                { icon:<IconBuilding />, name:'Corporate events', desc:"Photographers who turn around clean, professional images fast — headshots, panels, presentations, and candid networking moments that work for marketing and press.", spec:'Conferences · Summits · Team events · Retreats', d:'d2' },
                { icon:<IconGlass />, name:'Galas & private events', desc:"Photographers who excel in low-light, formal settings and know how to capture the atmosphere of an evening without disrupting it.", spec:'Galas · Fundraisers · Private dinners · Celebrations', d:'d3' },
                { icon:<IconMic />, name:'Concerts & performances', desc:"Fast-moving, high-energy events need photographers who understand stage lighting and can anticipate the moment before it happens.", spec:'Live music · Theater · Comedy · Spoken word', d:'d1' },
                { icon:<IconCap />, name:'Milestones & ceremonies', desc:"Graduations, award ceremonies, ribbon cuttings — events where the moment matters and the photography needs to honor it.", spec:'Graduations · Award nights · Grand openings', d:'d2' },
                { icon:<IconCamera />, name:'Venue & promotional', desc:"Showcase your space at its best. Photographers who specialize in architectural and interior work to help your venue stand out to future clients.", spec:'Interior · Exterior · Ambient · Marketing assets', d:'d3' },
              ].map((t, i) => (
                <div className={`ev-type-card ev-reveal ev-reveal-${t.d}`} key={i}>
                  <div className="ev-type-icon">{t.icon}</div>
                  <div className="ev-type-name">{t.name}</div>
                  <div className="ev-type-desc">{t.desc}</div>
                  <div className="ev-type-spec">{t.spec}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="ev-how-section">
          <div className="ev-section-inner">
            <div className="ev-reveal">
              <div className="ev-section-eyebrow">How it works</div>
              <h2 className="ev-section-title">From event brief to <em>confirmed photographer.</em></h2>
              <p className="ev-section-desc">Post your project in minutes. Browse by specialization. Both sides agree on the details before anything is locked in.</p>
            </div>
            <div className="ev-steps-grid">
              {[
                { num:1, active:true, title:'Post the event', body:'Describe the event type, date, location, and what needs to be covered. Photographers whose specialization matches will apply.', d:'d1' },
                { num:2, active:false, title:'Photographers apply — or you find one', body:'Qualified photographers apply to your project, or you can browse profiles by specialization and reach out to someone whose work fits the event.', d:'d2' },
                { num:3, active:false, title:'Both sides agree before anything is confirmed', body:'Scope, timeline, and rate are agreed on upfront. No surprises on the day. Payment is held in escrow until you approve the work.', d:'d3' },
                { num:4, active:false, title:'Receive and approve your photos', body:"Deliverables come through the platform. Review, approve, and release payment when you're happy with the work.", d:'' },
              ].map((s, i) => (
                <div className={`ev-reveal ev-reveal-${s.d}`} key={i} style={i === 3 ? {transitionDelay:'0.4s'} : {}}>
                  <div className={`ev-step-num${s.active ? ' ev-step-num-active' : ''}`}>{s.num}</div>
                  <div className="ev-step-title">{s.title}</div>
                  <div className="ev-step-body">{s.body}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY */}
        <section className="ev-why-section">
          <div className="ev-section-inner">
            <div className="ev-why-grid">
              <div className="ev-why-left ev-reveal">
                <div className="ev-section-eyebrow">Why FrameFinder</div>
                <h2 className="ev-section-title">Built for venues that<br />host <em>all kinds of events.</em></h2>
                <p className="ev-section-desc" style={{marginBottom:'40px'}}>You don't host one type of event — and you shouldn't have to maintain a different photographer contact for each one. FrameFinder gives you one place to find the right person, every time.</p>
                <Link to="/signup" className="ev-btn ev-btn-accent">Get started free →</Link>
              </div>
              <div className="ev-why-features ev-reveal" style={{transitionDelay:'0.15s'}}>
                {[
                  { icon:<IconTarget />, title:'Search by specialization', body:"Every photographer lists what they're built for. Filter by event type and find someone whose background matches what you're shooting — not just someone available that day." },
                  { icon:<IconShield />, title:'Every photographer is vetted', body:"We review portfolios and confirm professional experience before anyone goes live. You're choosing from a curated bench — not taking a chance on someone unknown." },
                  { icon:<IconHandshake />, title:'Everyone agrees before anything happens', body:"FrameFinder creates a standard project agreement so both sides are clear on scope, deliverables, timeline, and rate before the shoot is confirmed. No ambiguity, no assumptions." },
                  { icon:<IconEscrow />, title:'Escrow protects your payment', body:"Payment is held securely until you've reviewed and approved the photos. The photographer gets paid when the work is done right — and you have recourse if it isn't." },
                ].map((f, i) => (
                  <div className="ev-why-feature" key={i}>
                    <div className="ev-why-feature-icon">{f.icon}</div>
                    <div>
                      <div className="ev-why-feature-title">{f.title}</div>
                      <div className="ev-why-feature-body">{f.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* PHOTOGRAPHERS */}
        <section className="ev-photographers-section">
          <div className="ev-section-inner">
            <div className="ev-reveal" style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',gap:'20px',flexWrap:'wrap',marginBottom:'56px'}}>
              <div>
                <div className="ev-section-eyebrow">The photographers</div>
                <h2 className="ev-section-title" style={{marginBottom:0}}>Denver event photographers,<br /><em>across every specialty.</em></h2>
              </div>
              <Link to="/photographers" className="ev-btn ev-btn-ghost">Browse all photographers →</Link>
            </div>
            <div className="ev-photo-grid">
              {[
                { img:'ev-img-1', icon:<IconRings size={36} />, name:'Jessica Wong', spec:<><IconStar /> 5.0 · RiNo · 38 completed shoots</>, tags:['Weddings','Galas','Private events'], rate:'$220/hr', avail:'Available this month' },
                { img:'ev-img-2', icon:<IconBuilding size={36} />, name:'Rafael Diaz', spec:<><IconStar /> 4.9 · Capitol Hill · 29 completed shoots</>, tags:['Corporate','Conferences','Headshots'], rate:'$185/hr', avail:'Available this month' },
                { img:'ev-img-3', icon:<IconMic size={36} />, name:'Amara Osei', spec:<><IconStar /> 4.8 · Cherry Creek · 19 completed shoots</>, tags:['Performances','Milestones','Video'], rate:'$170/hr', avail:'Available this month' },
              ].map((p, i) => (
                <div className={`ev-photo-card ev-reveal ev-reveal-d${i+1}`} key={i}>
                  <div className={`ev-photo-card-img ${p.img}`}>{p.icon}</div>
                  <div className="ev-photo-card-body">
                    <div className="ev-photo-card-name">{p.name}</div>
                    <div className="ev-photo-card-spec">{p.spec}</div>
                    <div className="ev-photo-card-tags">{p.tags.map((t,j) => <span className="ev-tag" key={j}>{t}</span>)}</div>
                  </div>
                  <div className="ev-photo-card-footer">
                    <div className="ev-photo-card-rate">{p.rate}</div>
                    <div className="ev-avail"><div className="ev-avail-dot"></div>{p.avail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="ev-cta-section">
          <div className="ev-cta-inner ev-reveal">
            <div className="ev-cta-eyebrow">Get started · Free during beta</div>
            <h2 className="ev-cta-title">Find the right photographer<br />for your <em>next event.</em></h2>
            <p className="ev-cta-body">No subscription. No platform fee during beta. Post your event and find a vetted Denver photographer who specializes in exactly what you need.</p>
            <div className="ev-cta-acts">
              <Link to="/signup" className="ev-btn ev-btn-accent">Get started →</Link>
              <Link to="/photographers" className="ev-btn ev-btn-ghost" style={{background:'transparent',color:'rgba(255,255,255,0.5)',borderColor:'rgba(255,255,255,0.15)'}}>Browse photographers</Link>
            </div>
            <div className="ev-cta-disclaimer">Free during beta. No credit card required.</div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="ev-footer">
          <Link to="/" className="ev-footer-logo">FrameFinder</Link>
          <div className="ev-footer-links">
            <Link to="/photographers" className="ev-footer-link">Photographers</Link>
            <Link to="/signup" className="ev-footer-link">Get started</Link>
            <Link to="/help" className="ev-footer-link">Help</Link>
          </div>
          <div className="ev-footer-copy">© 2026 FrameFinder · Denver, CO</div>
        </footer>
      </div>
    </>
  );
};

export default Events;
